import type { LayoutPresetId, View } from "@gooner/shared";
import { LAYOUT_PRESETS, presetRects } from "@gooner/shared";

/**
 * The two things the editor can apply — a layout preset from the View panel's grid,
 * or a copy of a saved view — and whether applying one destroys work.
 *
 * The same click means two different things on an empty view and a view you have
 * been arranging for twenty minutes, and only one of them is safe. So the
 * distinction lives here, in a plain module, rather than as a condition buried in a
 * JSX handler where nothing can check it. (Quick setups left with the editor's
 * selector stage — they apply from the Home screen now.)
 */
export type LayoutChoice =
  | { kind: "preset"; id: LayoutPresetId }
  | { kind: "saved"; layout: View };

/**
 * True when applying the choice discards the feeds already on the subject's regions.
 *
 * A layout PRESET re-geometries: `relayoutRegions` copies each existing region onto the
 * new rect, so your collections and timers ride along and only the shape changes — which
 * is the thing you asked to change. A SAVED layout builds regions from scratch, so every
 * feed on screen is gone.
 */
export function replacesRegions(choice: LayoutChoice): boolean {
  return choice.kind === "saved";
}

/**
 * True when applying it would destroy work the user can see.
 *
 * Two ways that happens, and the second is not in the spec's binary:
 *
 *   • a replacing choice on a view that already has regions;
 *   • a layout preset with FEWER cells than the view has regions. `relayoutRegions` maps
 *     over the preset's rects, so its output is the preset's length — relaying four
 *     hand-placed regions onto a one-cell layout silently drops three. The spec calls a
 *     preset the non-destructive kind, and it is right about the common case; this is the
 *     corner where it isn't, and asking there costs one dialog and saves three feeds.
 *
 * An empty view can never lose anything, so nothing asks on the create path — which is
 * the path this selector was moved into the slot to serve.
 */
export function needsConfirm(choice: LayoutChoice, regionCount: number): boolean {
  if (regionCount === 0) return false;
  if (replacesRegions(choice)) return true;
  return choice.kind === "preset" && presetRects(choice.id).length < regionCount;
}

/** What the confirm dialog calls it. A dialog that cannot name the thing is unanswerable. */
export function choiceLabel(choice: LayoutChoice): string {
  if (choice.kind === "saved") return choice.layout.name;
  return LAYOUT_PRESETS.find((p) => p.id === choice.id)?.label ?? choice.id;
}

/**
 * How many regions applying the choice would leave on the canvas — what the Gold
 * region gate compares against its ceiling. A preset's output is exactly its rect
 * count (`relayoutRegions` maps over them); a saved layout brings its own regions
 * verbatim.
 */
export function choiceRegionCount(choice: LayoutChoice): number {
  if (choice.kind === "preset") return presetRects(choice.id).length;
  return choice.layout.regions.length;
}
